// frontend/src/pages/auth/CompleteRegistrationPage.js
import React, { useEffect, useState } from "react";
import axios from "axios";
import CustomerHeader from "../../components/CustomerHeader";
import { APP_FOOTER_LOGO_URL } from "../../config/branding";
import CustomerFooter from "../../components/CustomerFooter";
import "./LoginStyles.css";
import { TIMEZONE_OPTIONS } from "../../constants/timezones";

function CompleteRegistrationPage() {
  const [loading, setLoading] = useState(true);
  const [valid, setValid] = useState(false);
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [resendEmail, setResendEmail] = useState(
    new URLSearchParams(window.location.search).get("email") || "",
  );
  const [resendMessage, setResendMessage] = useState("");
  const [resendingLink, setResendingLink] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    phone: "",
    password: "",
    confirmPassword: "",
    timezone: "",
  });

  const token = new URLSearchParams(window.location.search).get("token");
  const isMobile = window.innerWidth <= 768;

  // The registrant's timezone is deliberately NOT detected or pre-filled.
  //
  // Pre-selecting a value - even a correct detection - lets them submit without
  // ever making the choice, and hides a WRONG detection (travel, VPN, mismatched
  // locale) behind a value that looks plausible. The field is required and must be
  // actively chosen (policy 2026-10-03).
  useEffect(() => {
    async function validate() {
      setLoading(true);
      setError("");
      try {
        const { data } = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/api/auth/complete-registration/validate`,
          { params: { token } },
        );
        if (data.ok) {
          setValid(true);
          setInfo(data);
        } else {
          setValid(false);
          setError("Invalid or expired registration link.");
        }
      } catch (err) {
        setValid(false);
        setError("Invalid or expired registration link.");
      } finally {
        setLoading(false);
      }
    }
    if (token) validate();
    else {
      setError("Missing registration token.");
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (resendCooldown <= 0) return;

    const timer = setInterval(() => {
      setResendCooldown((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);

    return () => clearInterval(timer);
  }, [resendCooldown]);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleResendCompleteRegistration = async () => {
    const email = resendEmail.trim();
    if (!email) {
      setError("Enter your email to request a new registration link.");
      return;
    }

    setResendingLink(true);
    setError("");
    setResendMessage("");

    try {
      const { data } = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/auth/complete-registration/resend`,
        { email },
      );

      setResendMessage(
        data?.message ||
          "Registration link sent. Please check your inbox and spam folder.",
      );
      setResendCooldown(Number(data?.cooldownSeconds) || 60);
    } catch (err) {
      const message =
        err?.response?.data?.message ||
        "Unable to resend registration link.";
      setError(message);
      if (err?.response?.data?.cooldownSeconds) {
        setResendCooldown(Number(err.response.data.cooldownSeconds));
      }
    } finally {
      setResendingLink(false);
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");

    // A referral agent is not a professional: no timezone, no scheduling setup.
    const isReferralAgent = info?.role === "R9";

    if (!form.firstName.trim() || !form.lastName.trim() || !form.phone.trim()) {
      setError("All fields are required.");
      return;
    }

    if (!isReferralAgent && !form.timezone) {
      setError("Please choose your timezone.");
      return;
    }

    if (form.password !== form.confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    // basic client-side complexity hint (server enforces again)
    const strong =
      form.password.length >= 8 &&
      /[A-Z]/.test(form.password) &&
      /[a-z]/.test(form.password) &&
      /\d/.test(form.password) &&
      /[^A-Za-z0-9]/.test(form.password);
    if (!strong) {
      setError("Use 8+ chars with upper, lower, number & special character.");
      return;
    }

    try {
      // A referral code captured by the /join/<code> share link, if this business arrived through one.
      let referralCode = null;
      try {
        referralCode = localStorage.getItem("ogestyle_referral_code");
      } catch (_) {
        referralCode = null;
      }

      const { data } = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/auth/complete-registration`,
        {
          token,
          ...(referralCode ? { referralCode } : {}),
          firstName: form.firstName,
          lastName: form.lastName,
          phone: form.phone,
          // An agent never picks a zone: scheduling is not part of their work.
          ...(info?.role === "R9" ? {} : { timezone: form.timezone }),
          password: form.password,
        },
      );
      if (data.ok) {
        const redirectPath =
          data.redirectPath ||
          (info?.role === "R8" ? "/login?role=R8" : "/login");
        alert(
          info?.role === "R9"
            ? "Referral agent registration complete. Please log in."
            : info?.role === "R8"
              ? "Support agent registration complete. Please log in."
              : "Registration complete. Please log in.",
        );
        window.location.href = redirectPath;
      } else {
        setError(data.message || "Unable to complete registration.");
      }
    } catch (err) {
      const errorData = err?.response?.data;
      let msg = "Unable to complete registration.";

      if (errorData?.error === "invalid_token") {
        msg =
          "Your registration link has expired. Please request a new invitation.";
      } else if (errorData?.error === "invalid_invite") {
        msg = "This invitation is no longer valid or has already been used.";
      } else if (errorData?.error === "weak_password") {
        msg = errorData.message || "Password does not meet requirements.";
      } else if (errorData?.message) {
        msg = errorData.message;
      } else if (err.message) {
        msg = err.message;
      }

      setError(msg);
    }
  };

  if (loading)
    return (
      <>
        <CustomerHeader />
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "calc(100vh - 140px)",
            padding: 24,
            fontSize: isMobile ? "14px" : "16px",
          }}
        >
          Validating link…
        </div>
        <CustomerFooter />
      </>
    );

  if (!valid)
    return (
      <>
        <CustomerHeader />
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "calc(100vh - 140px)",
            padding: 24,
            color: "#dc2626",
            fontSize: isMobile ? "14px" : "16px",
            textAlign: "center",
          }}
        >
          <div
            style={{
              width: "100%",
              maxWidth: 420,
              background: "#ffffff",
              borderRadius: 12,
              padding: 24,
              boxShadow: "0 10px 30px rgba(15, 23, 42, 0.08)",
              border: "1px solid #e5e7eb",
            }}
          >
            <div style={{ marginBottom: 16 }}>
              Invalid registration link or token expired.
            </div>
            <div
              style={{
                color: "#475569",
                fontSize: "14px",
                marginBottom: 12,
              }}
            >
              Enter your email and we will send a new complete-registration link if one is available.
            </div>
            <input
              type="email"
              value={resendEmail}
              onChange={(e) => setResendEmail(e.target.value)}
              placeholder="Enter your email"
              style={{
                width: "100%",
                padding: "12px 14px",
                borderRadius: 8,
                border: "1px solid #cbd5e1",
                marginBottom: 12,
                fontSize: "14px",
              }}
            />
            <button
              type="button"
              onClick={handleResendCompleteRegistration}
              disabled={resendingLink || resendCooldown > 0}
              style={{
                width: "100%",
                border: "none",
                borderRadius: 8,
                padding: "12px 14px",
                background:
                  resendingLink || resendCooldown > 0 ? "#94a3b8" : "#1a659e",
                color: "#fff",
                fontWeight: 600,
                cursor:
                  resendingLink || resendCooldown > 0
                    ? "not-allowed"
                    : "pointer",
              }}
            >
              {resendingLink
                ? "Sending..."
                : resendCooldown > 0
                  ? `Resend in ${resendCooldown}s`
                  : "Resend Complete-Registration Link"}
            </button>
            {resendMessage && (
              <div
                style={{
                  marginTop: 12,
                  color: "#166534",
                  fontSize: "13px",
                }}
              >
                {resendMessage}
              </div>
            )}
          </div>
        </div>
        <CustomerFooter />
      </>
    );

  return (
    <>
      <CustomerHeader />
      <div className="login-page">
        {/* Left Panel - Branding */}
        <div className="login-branding">
          <div className="branding-content">
            <div className="branding-logo"><img src={APP_FOOTER_LOGO_URL} alt="OGESTYLE" style={{ maxWidth: "220px", height: "auto" }} /></div>
            <p className="branding-subtitle">
              Join our platform of beauty professionals
            </p>
            <div className="branding-features">
              <div className="branding-feature">
                <div className="branding-feature-icon">📅</div>
                <span className="branding-feature-text">
                  Manage your bookings & schedule
                </span>
              </div>
              <div className="branding-feature">
                <div className="branding-feature-icon">💼</div>
                <span className="branding-feature-text">
                  Build your professional profile
                </span>
              </div>
              <div className="branding-feature">
                <div className="branding-feature-icon">💰</div>
                <span className="branding-feature-text">
                  Earn with secure payments
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Right Panel - Form */}
        <div className="login-form-panel">
          <div className="login-container">
            <h2
              style={{
                fontSize: "28px",
                fontWeight: "700",
                marginBottom: "8px",
                color: "#0f172a",
              }}
            >
              {info?.role === "R9"
                ? "Set Up Your Referral Account"
                : "Complete Your Registration"}
            </h2>
            <p
              style={{
                fontSize: "14px",
                color: "#64748b",
                marginBottom: "24px",
              }}
            >
              {info?.role === "R9"
                ? "Your name and a password — that's all we need"
                : "Just a few more details to get started"}
            </p>

            {info?.shop && (
              <div
                style={{
                  background: "#f3f4f6",
                  padding: isMobile ? "12px" : "16px",
                  borderRadius: "8px",
                  marginBottom: "16px",
                  fontSize: isMobile ? "13px" : "14px",
                }}
              >
                <p style={{ color: "#374151", margin: "0 0 8px" }}>
                  You're being added to <strong>{info.shop.name}</strong>
                </p>
                <p style={{ color: "#6b7280", margin: 0 }}>
                  Email: <strong>{info?.email}</strong>
                </p>
              </div>
            )}

            {error && (
              <div
                style={{
                  background: "#fef2f2",
                  border: "1px solid #fecaca",
                  borderLeft: "4px solid #dc2626",
                  padding: isMobile ? "10px 12px" : "12px 16px",
                  marginBottom: 16,
                  borderRadius: "6px",
                  fontSize: isMobile ? "13px" : "14px",
                }}
              >
                <span style={{ color: "#dc2626" }}>{error}</span>
              </div>
            )}

            <form onSubmit={onSubmit}>
              <div style={{ display: "grid", gap: 12 }}>
                <input
                  name="firstName"
                  type="text"
                  placeholder="First name"
                  value={form.firstName}
                  onChange={onChange}
                  required
                  style={{
                    padding: isMobile ? "12px" : "14px",
                    fontSize: isMobile ? "15px" : "16px",
                    border: "2px solid #e5e7eb",
                    borderRadius: "8px",
                    width: "100%",
                    boxSizing: "border-box",
                  }}
                />
                <input
                  name="lastName"
                  type="text"
                  placeholder="Last name"
                  value={form.lastName}
                  onChange={onChange}
                  required
                  style={{
                    padding: isMobile ? "12px" : "14px",
                    fontSize: isMobile ? "15px" : "16px",
                    border: "2px solid #e5e7eb",
                    borderRadius: "8px",
                    width: "100%",
                    boxSizing: "border-box",
                  }}
                />
                <input
                  name="phone"
                  type="tel"
                  placeholder="Phone number *"
                  value={form.phone}
                  onChange={onChange}
                  required
                  style={{
                    padding: isMobile ? "12px" : "14px",
                    fontSize: isMobile ? "15px" : "16px",
                    border: "2px solid #e5e7eb",
                    borderRadius: "8px",
                    width: "100%",
                    boxSizing: "border-box",
                  }}
                />
                {info?.role !== "R9" && (
                  <div>
                  <select
                    name="timezone"
                    value={form.timezone}
                    onChange={onChange}
                    required
                    style={{
                      padding: isMobile ? "12px" : "14px",
                      fontSize: isMobile ? "15px" : "16px",
                      border: "2px solid #e5e7eb",
                      borderRadius: "8px",
                      width: "100%",
                      boxSizing: "border-box",
                      backgroundColor: "white",
                    }}
                  >
                    <option value="">Select your timezone...</option>
                    {TIMEZONE_OPTIONS.map((tz) => (
                      <option key={tz.value} value={tz.value}>
                        {tz.label}
                      </option>
                    ))}
                  </select>
                  <div
                      style={{
                        fontSize: "11px",
                        color: "#6b7280",
                        marginTop: "4px",
                      }}
                    >
                      Please choose your timezone for accurate scheduling.
                    </div>
                  </div>
                )}
                <input
                  name="password"
                  type="password"
                  placeholder="Create password"
                  value={form.password}
                  onChange={onChange}
                  required
                  style={{
                    padding: isMobile ? "12px" : "14px",
                    fontSize: isMobile ? "15px" : "16px",
                    border: "2px solid #e5e7eb",
                    borderRadius: "8px",
                    width: "100%",
                    boxSizing: "border-box",
                  }}
                />
                <input
                  name="confirmPassword"
                  type="password"
                  placeholder="Confirm password"
                  value={form.confirmPassword}
                  onChange={onChange}
                  required
                  style={{
                    padding: isMobile ? "12px" : "14px",
                    fontSize: isMobile ? "15px" : "16px",
                    border: "2px solid #e5e7eb",
                    borderRadius: "8px",
                    width: "100%",
                    boxSizing: "border-box",
                  }}
                />
              </div>

              <div
                style={{
                  fontSize: isMobile ? "11px" : "12px",
                  color: "#6b7280",
                  marginTop: "12px",
                  padding: "8px",
                  background: "#f9fafb",
                  borderRadius: "6px",
                }}
              >
                Password must be 8+ characters with uppercase, lowercase, number
                & special character
              </div>

              <div style={{ marginTop: 16 }}>
                <button
                  type="submit"
                  style={{
                    width: "100%",
                    background: "#00509d",
                    color: "#fff",
                    padding: isMobile ? "12px" : "14px",
                    borderRadius: "8px",
                    border: "none",
                    cursor: "pointer",
                    fontSize: isMobile ? "15px" : "16px",
                    fontWeight: "600",
                  }}
                >
                  Finish Registration
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
      <CustomerFooter />
    </>
  );
}

export default CompleteRegistrationPage;
